import React from 'react';
import { CalculationMode, ValidationErrors } from '../../types/gasTypes';
import Input from './common/Input';

/**
 * Props interface for the TargetSettings component
 */
interface TargetSettingsProps {
  /** Current calculation mode */
  calculationMode: CalculationMode;
  /** Target alcohol percentage value */
  targetAlcoholPercent: number | string;
  /** Target octane rating value */
  targetOctane: number | string;
  /** Budget value */
  budget: number | string;
  /** Validation errors */
  validationErrors: ValidationErrors;
  /** Handler for target alcohol percentage changes */
  onTargetAlcoholChange: (value: number | string) => void;
  /** Handler for target octane changes */
  onTargetOctaneChange: (value: number | string) => void;
  /** Handler for budget changes */
  onBudgetChange: (value: string) => void;
}

/**
 * TargetSettings Component
 * Renders target settings inputs for alcohol/octane target and budget
 */
const TargetSettings: React.FC<TargetSettingsProps> = ({
  calculationMode,
  targetAlcoholPercent,
  targetOctane,
  budget,
  validationErrors,
  onTargetAlcoholChange,
  onTargetOctaneChange,
  onBudgetChange
}) => {
  // Handle target alcohol percentage change
  const handleAlcoholChange = (value: string) => {
    onTargetAlcoholChange(value === '' ? '' : parseFloat(value) || 0);
  };

  // Handle target octane change
  const handleOctaneChange = (value: string) => {
    onTargetOctaneChange(value === '' ? '' : parseFloat(value) || 0);
  };

  return (
    <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-5 md:mb-6 px-1 sm:px-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Target Input - Conditional based on calculation mode */}
        {calculationMode === 'alcohol' ? (
          <Input
            label="Target Alcohol %"
            type="number"
            value={targetAlcoholPercent}
            onChange={handleAlcoholChange}
            min="0"
            max="100"
            step="0.1"
            suffix="%"
            error={validationErrors.targetAlcoholPercent}
            className={`text-lg ${
              validationErrors.targetAlcoholPercent ? 'border-red-500' : ''
            }`}
          />
        ) : (
          <Input
            label="Target Octane"
            type="number"
            value={targetOctane}
            onChange={handleOctaneChange}
            min="80"
            max="120"
            step="0.1"
            error={validationErrors.targetOctane}
            className={`text-lg focus:ring-purple-500 focus:border-purple-500 ${
              validationErrors.targetOctane ? 'border-red-500' : ''
            }`}
          />
        )}

        {/* Budget Input */}
        <Input
          label="Budget"
          type="number"
          value={budget}
          onChange={onBudgetChange}
          min="0"
          step="0.01"
          placeholder="50.00"
          suffix="€"
          error={validationErrors.budget}
          className={`text-lg ${
            calculationMode === 'alcohol' 
              ? 'focus:ring-blue-500 focus:border-blue-500' 
              : 'focus:ring-purple-500 focus:border-purple-500'
          } ${validationErrors.budget ? 'border-red-500' : ''}`}
        />
      </div>
    </div>
  );
};

export default TargetSettings;